import {
    effectiveRolePermissions,
    flattenPermissions,
    PermissionString,
    RBAC_PERMISSIONS_VERSION,
    RBACAction,
    RBACModule,
    rolePermissionCheck,
    StoredRolePermissions,
} from '@numeo/types-core'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import { useIsBoxTruckCompany } from './useIsBoxTruckCompany'

/**
 * Permission check result interface
 */
interface UsePermissionsResult {
    hasPermission: (module: RBACModule, action: RBACAction) => boolean
    canAccessModule: (module: RBACModule) => boolean
    hasAnyPermission: (perms: Array<{ module: RBACModule; action: RBACAction }>) => boolean
    hasAllPermissions: (perms: Array<{ module: RBACModule; action: RBACAction }>) => boolean
    isOwner: boolean
    isAdmin: boolean
    currentRole: { _id: string; name: string; permissions: PermissionString[] } | null
    permissions: PermissionString[]
    /** Sidebar rows the caller's role restricts them to, or `undefined` for no restriction. Presentation
     *  only — see `RBACRole.visibleMenuIds`; what a role may REACH is still `permissions`. */
    visibleMenuIds?: string[]
}

/**
 * Hook to check user permissions
 */
export function usePermissions(): UsePermissionsResult {
    // Get application and user data from Redux
    const application = useSelector((state: RootState) => state.auth.application)
    const user = useSelector((state: RootState) => state.auth.user)
    // "Does this company run Box Truck" — answered by the server on `/box-truck-loads/access`, because the
    // Gluon owner allowlist that decides it never reaches the browser. It makes the built-in Dispatcher
    // role read-only on Ongoing Loads; a custom role is unaffected. See `resolveModulePermission`.
    // Optional chain on purpose: this hook is rendered by callers that mount a partial store (and by
    // tests that build one), and a permission check must not throw because a slice it only consults is
    // absent. Missing reads as "not a box truck company", which is the direction that changes nothing.
    const isBoxTruckApp = useIsBoxTruckCompany()

    // Memoize computed values
    const result = useMemo(() => {
        // Get custom roles directly from application context (populated via setApplication during auth)
        const rbacRoles = application?.rbac?.roles || []

        if (!application || !user) {
            return {
                hasPermission: () => false,
                canAccessModule: () => false,
                hasAnyPermission: () => false,
                hasAllPermissions: () => false,
                isOwner: false,
                isAdmin: false,
                currentRole: null,
                rbacEnabled: false,
                permissions: [],
                visibleMenuIds: undefined,
            }
        }

        // Check if user is owner
        const isOwner = String(application.owner) === String(user.id)

        // Get user's RBAC role if assigned
        let currentRole: { _id: string; name: string; permissions: PermissionString[] } | null = null
        let permissions: PermissionString[] = []
        // The OWNER is never restricted. They already bypass every permission check below, and more to the
        // point: a menu selection is undone from Settings, so an owner who applied one to themselves would
        // otherwise have hidden the only door back out. Everyone else keeps what their role says.
        let visibleMenuIds: string[] | undefined
        // The stored role the checks read: the custom role, the system template, or neither (hardcoded defaults).
        let storedRole: StoredRolePermissions | undefined

        if (user.rbacRoleId) {
            // Check custom roles from application context
            const role = rbacRoles.find((r) => String(r._id) === String(user.rbacRoleId))
            // A role id that points at no role grants nothing (not even the legacy Organization grants), the
            // same as the server's empty list.
            storedRole = role ?? { permissions: [], permissionsVersion: RBAC_PERMISSIONS_VERSION }
            if (role) {
                permissions = flattenPermissions(effectiveRolePermissions(role, user.role))
                if (!isOwner && role.visibleMenuIds?.length) visibleMenuIds = role.visibleMenuIds
                currentRole = {
                    _id: String(role._id),
                    name: role.name,
                    permissions,
                }
            }
        } else {
            // For system roles, check if templates exist
            const systemRoleTemplates = application?.rbac?.systemRoleTemplates || []
            const systemRoleTemplate = systemRoleTemplates.find((template) => template.userRole === user.role)

            if (systemRoleTemplate) {
                // Use system role template
                storedRole = systemRoleTemplate
                permissions = flattenPermissions(effectiveRolePermissions(systemRoleTemplate))
                // Read the same way as a custom role, so the two kinds of role behave identically. The
                // ADMIN template is `isEditable: false` server-side, so the role that can undo a selection
                // can never carry one.
                if (!isOwner && systemRoleTemplate.visibleMenuIds?.length) visibleMenuIds = systemRoleTemplate.visibleMenuIds
                currentRole = {
                    _id: String(systemRoleTemplate._id),
                    name: systemRoleTemplate.name,
                    permissions,
                }
            }
        }

        const isAdmin = user.role === 0 // UserRole.ADMIN = 0
        // Read once per role: every row and control asks its own question of it.
        const roleAllows = rolePermissionCheck(user.role, storedRole, { isBoxTruckApp })
        const hasPermission = (module: RBACModule, action: RBACAction): boolean => {
            // Tier 1: Owner Bypass
            if (isOwner) {
                return true
            }

            // Tier 2: custom role, then stored system template, then the hardcoded system-role defaults
            return roleAllows(module, action)
        }

        const canAccessModule = (module: RBACModule): boolean => Object.values(RBACAction).some((action) => hasPermission(module, action))

        const hasAnyPermission = (perms: Array<{ module: RBACModule; action: RBACAction }>): boolean => {
            return perms.some((p) => hasPermission(p.module, p.action))
        }

        const hasAllPermissions = (perms: Array<{ module: RBACModule; action: RBACAction }>): boolean => {
            return perms.every((p) => hasPermission(p.module, p.action))
        }

        return {
            hasPermission,
            canAccessModule,
            hasAnyPermission,
            hasAllPermissions,
            isOwner,
            isAdmin,
            currentRole,
            permissions,
            visibleMenuIds,
        }
    }, [application, user, isBoxTruckApp])

    return result
}
